import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentPortfolioSettingsOverviewData,
  type EnrollmentPortfolioSettingsOverviewSubmitInput,
  type EnrollmentPortfolioSettingsOverviewSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment PortfolioSettingsOverview data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPortfolioSettingsOverviewData>
 * @example const response = await getEnrollmentPortfolioSettingsOverviewData()
 */
export const getEnrollmentPortfolioSettingsOverviewData = async (
  context?: DefaultContext,
): Promise<EnrollmentPortfolioSettingsOverviewData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentPortfolioSettingsOverviewData',
      query: `mutation getEnrollmentPortfolioSettingsOverviewData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentPortfolioSettingsOverviewData }
  return response.submitEnrollment
}

export const useGetEnrollmentPortfolioSettingsOverviewData = createQueryHook<
  undefined,
  EnrollmentPortfolioSettingsOverviewData
>(getEnrollmentPortfolioSettingsOverviewData)

/**
 * Submit Enrollment PortfolioSettingsOverview data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPortfolioSettingsOverviewSubmitOutput>
 * @example const response = await submitEnrollmentPortfolioSettingsOverviewData();
 */
export const submitEnrollmentPortfolioSettingsOverviewData = async (
  enrollmentPortfolioSettingsOverviewSubmitInput: EnrollmentPortfolioSettingsOverviewSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentPortfolioSettingsOverviewSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentPortfolioSettingsOverviewData',
      query: `mutation submitEnrollmentPortfolioSettingsOverviewData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentPortfolioSettingsOverviewSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentPortfolioSettingsOverviewSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentPortfolioSettingsOverviewData = createMutationHook<
  EnrollmentPortfolioSettingsOverviewSubmitInput,
  EnrollmentPortfolioSettingsOverviewSubmitOutput
>(submitEnrollmentPortfolioSettingsOverviewData)
